import { useState } from 'react'
import { AidStation, Segment, CalibrationProfile, CheckInInput, RunWithDetails } from '../../types'
import { generateFullPlanPDF } from '../../lib/pdf-export/generators'
import { useCrewCheckIns } from '../../hooks/useCrewCheckIns'
import { CrewCalculations, CrewETACalculation } from '../../lib/crewCalculations'
import { ETAWindow, CompactETAWindow, ETACountdown } from './ETAWindow'
import { CheckInDialog, QuickCheckInButtons } from './CheckInDialog'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Separator } from '../ui/separator'
import { Alert, AlertDescription } from '../ui/alert'
import { 
  MapPin, 
  Clock, 
  CheckCircle2, 
  Plus, 
  Trash2, 
  Share2,
  QrCode,
  RefreshCw,
  AlertCircle,
  Package,
  Printer
} from 'lucide-react'

interface CrewAidStationsViewProps {
  aidStations: AidStation[]
  segments: Segment[]
  calibrationProfile?: CalibrationProfile | null
  slug: string
  startTime?: Date
  showElapsed?: boolean
  className?: string
  run?: RunWithDetails
}

export function CrewAidStationsView({
  aidStations,
  segments,
  calibrationProfile,
  slug,
  startTime,
  showElapsed = false,
  className = '',
  run
}: CrewAidStationsViewProps) {
  const [selectedStation, setSelectedStation] = useState<AidStation | null>(null)
  const [isCheckInDialogOpen, setIsCheckInDialogOpen] = useState(false)
  const [showShareDialog, setShowShareDialog] = useState(false)

  // Ensure finish line aid station exists and sort by distance
  const stationsWithFinish = CrewCalculations.ensureFinishLineStation(aidStations, segments)
  
  const {
    checkIns,
    isLoading,
    error,
    addCheckIn,
    removeCheckIn,
    hasCheckIn,
    getCheckIn,
    shareUrl,
    refresh
  } = useCrewCheckIns(slug, stationsWithFinish, startTime)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-gray-400" />
          <p className="text-gray-600">Loading crew data...</p>
        </div>
      </div>
    )
  }

  if (!aidStations?.length) {
    return (
      <Card className={className}>
        <CardContent className="p-8 text-center">
          <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Aid Stations</h3>
          <p className="text-gray-600">This run doesn't have any aid stations configured.</p>
        </CardContent>
      </Card>
    )
  }

  const sortedStations = [...stationsWithFinish].sort((a, b) => a.at_distance_km - b.at_distance_km)

  // Calculate ETAs for all stations
  const etaCalculations = new Map<string, CrewETACalculation>()
  for (const station of sortedStations) {
    const calc = CrewCalculations.calculateAidStationETA(
      station,
      segments,
      calibrationProfile,
      checkIns,
      startTime,
      aidStations
    )
    etaCalculations.set(station.id, calc)
  }

  const handleCheckIn = async (input: CheckInInput): Promise<boolean> => {
    if (!selectedStation) return false
    const success = await addCheckIn(selectedStation.id, input)
    if (success) {
      // Force a refresh to ensure UI updates
      refresh()
    }
    return success
  }

  const handleQuickCheckIn = async (method: 'now' | 'quick-late' | 'quick-early') => {
    if (!selectedStation || !startTime) return

    let input: CheckInInput
    const now = new Date()

    switch (method) {
      case 'now':
        input = { method: 'now' }
        break
      case 'quick-early':
        const earlyTime = new Date(now.getTime() - 5 * 60 * 1000) // 5 minutes ago
        input = { 
          method: 'clock', 
          clockTime: earlyTime.toTimeString().slice(0, 5) 
        }
        break
      case 'quick-late':
        const lateTime = new Date(now.getTime() + 5 * 60 * 1000) // 5 minutes from now
        input = { 
          method: 'clock', 
          clockTime: lateTime.toTimeString().slice(0, 5) 
        }
        break
    }

    const success = await addCheckIn(selectedStation.id, input)
    if (success) {
      // Force a refresh to ensure UI updates
      refresh()
    }
  }

  const handleRemoveCheckIn = (stationId: string) => {
    removeCheckIn(stationId)
    // Force a refresh to ensure UI updates
    refresh()
  }

  const handleStationClick = (station: AidStation) => {
    setSelectedStation(station)
    setIsCheckInDialogOpen(true)
  }

  const handleExportPDF = async () => {
    if (!run) {
      alert('Run data not available for PDF export')
      return
    }

    try {
      const blob = await generateFullPlanPDF(run)
      
      // Create download link
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${run.title || 'ultraplan'}-full-plan.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('PDF export failed:', error)
      alert('Failed to export PDF. Please try again.')
    }
  }

  const nextStation = CrewCalculations.getNextAidStation(sortedStations, checkIns)
  const progress = CrewCalculations.getRaceProgress(sortedStations, segments, checkIns)

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Error Display */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Race Progress Summary */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 progress-summary-print">
        <CardContent className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Race Progress</h2>
              <div className="space-y-1">
                <div className="text-lg">
                  <span className="font-semibold">{progress.completedDistance.toFixed(1)} km</span>
                  <span className="text-gray-600"> of {progress.totalDistance.toFixed(1)} km</span>
                </div>
                <div className="text-sm text-gray-600">
                  {progress.progressPercent.toFixed(1)}% complete
                </div>
              </div>
            </div>
            <div className="text-right space-y-2">
              <div className="flex space-x-2">
                {run && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleExportPDF}
                  >
                    <Printer className="h-4 w-4 mr-1" />
                    Print PDF
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowShareDialog(!showShareDialog)}
                >
                  <Share2 className="h-4 w-4 mr-1" />
                  Share
                </Button>
              </div>
              {showShareDialog && (
                <div className="absolute right-0 mt-2 p-3 bg-white border rounded-lg shadow-lg z-10 min-w-[200px]">
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Share crew updates</p>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigator.clipboard.writeText(shareUrl)}
                      >
                        Copy Link
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          // TODO: Generate QR code
                          alert('QR code functionality coming soon!')
                        }}
                      >
                        <QrCode className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {nextStation && (
            <div className="bg-white rounded-lg p-4 border">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-600">Next Station</p>
                  <p className="text-lg font-semibold">{nextStation.name}</p>
                  <p className="text-sm text-gray-500">{nextStation.at_distance_km.toFixed(1)} km</p>
                </div>
                <div className="text-right">
                  {etaCalculations.has(nextStation.id) && (
                    <ETACountdown 
                      window={etaCalculations.get(nextStation.id)!.window}
                      startTime={startTime}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Aid Stations Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 print-grid">
        {sortedStations.map((station) => {
          const checkIn = getCheckIn(station.id)
          const hasCheckedIn = hasCheckIn(station.id)
          const etaCalc = etaCalculations.get(station.id)
          
          return (
            <Card 
              key={station.id} 
              className={`
                cursor-pointer transition-all hover:shadow-lg crew-aid-station-card
                ${hasCheckedIn ? 'bg-green-50 border-green-200 checked-in' : 'hover:border-blue-300'}
                print:break-inside-avoid print:mb-4
              `}
              onClick={() => handleStationClick(station)}
            >
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="flex items-center space-x-2 text-lg">
                      <MapPin className="h-5 w-5 text-gray-500" />
                      <span>{station.name}</span>
                      {hasCheckedIn && (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      )}
                    </CardTitle>
                    <p className="text-sm text-gray-600 mt-1">
                      {station.at_distance_km.toFixed(1)} km
                    </p>
                  </div>
                  <div className="text-right">
                    {etaCalc && (
                      <CompactETAWindow
                        window={etaCalc.window}
                        startTime={startTime}
                        showElapsed={showElapsed}
                        status={etaCalc.status}
                      />
                    )}
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* ETA Details */}
                {etaCalc && !hasCheckedIn && (
                  <ETAWindow
                    window={etaCalc.window}
                    startTime={startTime}
                    showElapsed={showElapsed}
                    status={etaCalc.status}
                    speedFactor={etaCalc.speedFactor}
                  />
                )}

                {/* Check-in Info */}
                {checkIn && (
                  <div className="bg-green-100 rounded-lg p-3 checkin-status-print">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium text-green-800">
                          ✅ Checked in at {CrewCalculations.formatElapsedTime(checkIn.actualTimeMinutes)}
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          {checkIn.timestamp.toLocaleTimeString()}
                        </p>
                        {checkIn.notes && (
                          <p className="text-sm text-green-700 mt-2 italic">
                            "{checkIn.notes}"
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRemoveCheckIn(station.id)
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                {!hasCheckedIn && startTime && (
                  <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
                    <QuickCheckInButtons
                      onCheckIn={(method) => {
                        setSelectedStation(station)
                        handleQuickCheckIn(method)
                      }}
                      hasStartTime={!!startTime}
                      className="flex-1"
                    />
                    <Button
                      size="default"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleStationClick(station)
                      }}
                      className="py-2 px-4 text-sm font-medium touch-manipulation min-h-[40px]"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Check In
                    </Button>
                  </div>
                )}

                {/* Station Details */}
                <Separator />
                <div className="space-y-2">
                  {(station.planned_stop_min || 0) > 0 && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Clock className="h-4 w-4" />
                      <span>Planned stop: {station.planned_stop_min} min</span>
                    </div>
                  )}
                  
                  {Array.isArray(station.available) && station.available.length > 0 && (
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Package className="h-4 w-4" />
                        <span>Available:</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {station.available.slice(0, 6).map((item, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {String(item)}
                          </Badge>
                        ))}
                        {station.available.length > 6 && (
                          <Badge variant="secondary" className="text-xs">
                            +{station.available.length - 6} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {station.notes && (
                    <p className="text-sm text-gray-600 italic">
                      {station.notes}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Check-in Dialog */}
      <CheckInDialog
        isOpen={isCheckInDialogOpen}
        onClose={() => {
          setIsCheckInDialogOpen(false)
          setSelectedStation(null)
        }}
        onSubmit={handleCheckIn}
        aidStation={selectedStation}
        hasStartTime={!!startTime}
        error={error}
      />
    </div>
  )
}